"use client";

import {
  ChevronDown,
  Globe,
  Menu,
  Moon,
  Package,
  Phone,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Sun,
  Truck,
  User,
  X
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { SearchBar } from "@/components/layout/search-bar";
import { useCartStore } from "@/components/providers/cart-store";
import { MovaLogo } from "@/components/ui/mova-logo";
import { categories as fallbackCategories } from "@/lib/data";
import { cn } from "@/lib/utils";

type NavCategoryItem = {
  id: string;
  name: string;
  slug: string;
  href: string;
};

type SpecialLink = {
  visible: boolean;
  label: string;
  href: string;
};

type CustomLink = {
  id: string;
  label: string;
  href: string;
  visible: boolean;
};

type HeaderNavState = {
  showAllButton: boolean;
  categories: NavCategoryItem[];
  specialLinks: {
    deals: SpecialLink;
    prime: SpecialLink;
    compare: SpecialLink;
  };
  customLinks: CustomLink[];
};

const DEFAULT_NAV: HeaderNavState = {
  showAllButton: true,
  categories: fallbackCategories.slice(0, 9).map((name) => ({
    id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    name,
    slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    href: `/search?category=${encodeURIComponent(name)}`
  })),
  specialLinks: {
    deals: { visible: true, label: "Today's Deals", href: "/search?deal=flash" },
    prime: { visible: false, label: "Prime", href: "/search" },
    compare: { visible: true, label: "Compare", href: "/compare" }
  },
  customLinks: []
};

export function Navbar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session } = useSession();
  const { theme, setTheme } = useTheme();
  const cartCount = useCartStore((s) => s.count());
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const [lang, setLang] = useState("EN");

  // Automatically close mobile menu and account dropdown on route change
  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [pathname, searchParams]);

  // Click outside to close account dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Dynamic header navigation configured by Admin
  const [navData, setNavData] = useState<HeaderNavState>(DEFAULT_NAV);

  useEffect(() => {
    setMounted(true);

    async function loadNav() {
      try {
        const res = await fetch("/api/nav");
        if (res.ok) {
          const data = await res.json();
          if (data?.nav) {
            setNavData(data.nav);
          }
        }
      } catch (err) {
        console.error("Error fetching header navigation:", err);
      }
    }

    loadNav();

    // Listen for live updates dispatched when admin updates settings
    function handleNavUpdate() {
      loadNav();
    }
    window.addEventListener("nav-updated", handleNavUpdate);
    return () => window.removeEventListener("nav-updated", handleNavUpdate);
  }, []);

  const displayCartCount = mounted ? cartCount : 0;

  return (
    <header className="sticky top-0 z-50 transition-all">

      {/* Main Navbar Bar */}
      <div className="relative z-30 border-b border-slate-200/90 bg-white/95 text-[#172B3A] shadow-xs backdrop-blur-md transition-all dark:border-white/10 dark:bg-slate-900/95 dark:text-white">
        <div className="relative z-20 mx-auto flex max-w-[1500px] items-center gap-2 px-2 py-2 sm:gap-3 sm:px-3">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-2 text-[#073B70] hover:bg-slate-100 dark:text-white dark:hover:bg-white/10 lg:hidden"
            aria-label="Open menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Official MOVA Logo */}
          <Link href="/" className="flex shrink-0 items-center rounded-lg px-1 py-0.5 transition-opacity hover:opacity-95" aria-label="MOVA MARKET ETH Home">
            <MovaLogo size="md" showTagline={false} />
          </Link>


          <SearchBar className="hidden md:flex md:ml-6 lg:ml-8 max-w-md" initialQuery={searchParams.get("q") ?? ""} />

          <div className="ml-auto flex items-center gap-1 sm:gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setLang((l) => (l === "EN" ? "AM" : "EN"))}
              className="hidden items-center gap-0.5 rounded-lg px-2 py-1 text-xs font-bold text-[#073B70] hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10 lg:flex"
              aria-label="Change language"
            >
              <Globe className="h-4 w-4 text-[#087DB5]" />
              <span>{lang}</span>
              <ChevronDown className="h-3 w-3" />
            </button>

            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="hidden rounded-lg p-2 text-[#073B70] hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10 sm:inline-flex"
              aria-label="Toggle theme"
            >
              {mounted && theme === "dark" ? <Sun className="h-5 w-5 text-[#FF8A00]" /> : <Moon className="h-5 w-5 text-[#073B70]" />}
            </button>

            <div
              ref={accountRef}
              className="group relative z-30 hidden lg:block"
              onMouseEnter={() => setAccountOpen(true)}
              onMouseLeave={() => setAccountOpen(false)}
            >
              <button
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                className="block rounded-lg px-2 py-1 text-left hover:bg-slate-100 dark:hover:bg-white/10 transition-colors cursor-pointer"
                aria-expanded={accountOpen}
                aria-haspopup="true"
              >
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                  Hello, {session?.user?.name?.split(" ")[0] ?? "Sign in"}
                </span>
                <span className="flex items-center gap-0.5 text-xs sm:text-sm font-bold text-[#073B70] dark:text-white">
                  Account &amp; Lists
                  <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", accountOpen && "rotate-180")} />
                </span>
              </button>

              <div
                className={cn(
                  "absolute right-0 top-full z-50 pt-1.5 w-60 transition-all duration-150",
                  accountOpen
                    ? "visible opacity-100 translate-y-0 pointer-events-auto"
                    : "invisible opacity-0 translate-y-1 pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto"
                )}
              >
                <div className="rounded-xl border border-slate-200 bg-white p-2 text-slate-900 shadow-xl dark:border-white/10 dark:bg-slate-900 dark:text-white">
                  {session ? (
                    <>
                      <Link
                        href="/dashboard/profile"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                      >
                        Your Account
                      </Link>
                      <Link
                        href="/dashboard/orders"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-slate-800 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                      >
                        Your Orders
                      </Link>
                      <Link
                        href="/dashboard/wishlist"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-slate-800 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                      >
                        Wishlist
                      </Link>
                      <Link
                        href="/dashboard/listings"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-slate-800 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                      >
                        My Phone/PC Listings
                      </Link>
                      <Link
                        href="/sell"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm font-semibold text-[#FF8A00] transition-colors hover:bg-amber-50 dark:hover:bg-white/10"
                      >
                        + Sell Phone or PC (Free)
                      </Link>
                      {session.user.role === "VENDOR" && (
                        <Link
                          href="/vendor"
                          onClick={() => setAccountOpen(false)}
                          className="block rounded-lg px-3 py-2 text-sm font-bold text-[#087DB5] transition-colors hover:bg-[#087DB5]/10 dark:hover:bg-white/10"
                        >
                          Vendor Dashboard
                        </Link>
                      )}
                      {session.user.role === "ADMIN" && (
                        <Link
                          href="/admin"
                          onClick={() => setAccountOpen(false)}
                          className="block rounded-lg px-3 py-2 text-sm font-bold text-[#073B70] transition-colors hover:bg-slate-100 dark:text-white dark:hover:bg-white/10"
                        >
                          Admin Console
                        </Link>
                      )}
                      {session.user.role === "ADMIN1" && (
                        <Link
                          href="/admin1"
                          onClick={() => setAccountOpen(false)}
                          className="block rounded-lg px-3 py-2 text-sm font-bold text-[#FF8A00] transition-colors hover:bg-amber-50 dark:hover:bg-white/10"
                        >
                          Admin 1 Console
                        </Link>
                      )}
                      <div className="my-1 border-t border-slate-200/80 dark:border-white/10" />
                      <button
                        type="button"
                        onClick={() => {
                          setAccountOpen(false);
                          signOut({ callbackUrl: "/" });
                        }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-white/10 cursor-pointer"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <div className="p-1">
                      <Link
                        href="/login"
                        onClick={() => setAccountOpen(false)}
                        className="block rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] px-3 py-2.5 text-center text-sm font-bold text-white shadow-xs hover:from-[#098dc9] hover:to-[#084582] transition-colors"
                      >
                        Sign in
                      </Link>
                      <p className="mt-2 px-3 text-center text-xs text-slate-500 dark:text-slate-400">
                        New customer?{" "}
                        <Link
                          href="/signup"
                          onClick={() => setAccountOpen(false)}
                          className="text-[#087DB5] font-bold hover:underline"
                        >
                          Start here
                        </Link>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Link href="/dashboard/orders" className="hidden rounded-lg px-2 py-1 text-left hover:bg-slate-100 dark:hover:bg-white/10 lg:block transition-colors">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Returns</span>
              <span className="block text-xs sm:text-sm font-bold text-[#073B70] dark:text-white">& Orders</span>
            </Link>

            {/* Desktop Cart Button (Desktop only) */}
            <Link
              href="/cart"
              suppressHydrationWarning
              className="relative hidden lg:flex items-center gap-1.5 rounded-xl bg-[#087DB5]/10 px-2.5 py-1.5 text-[#073B70] hover:bg-[#087DB5]/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 transition-colors"
              aria-label={`Cart, ${displayCartCount} items`}
            >
              <div className="relative">
                <ShoppingCart className="h-6 w-6 sm:h-7 sm:w-7 text-[#073B70] dark:text-white" />
                <span
                  suppressHydrationWarning
                  className="absolute -top-1.5 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-[#FF8A00] flex items-center justify-center text-[11px] font-bold text-white shadow-2xs"
                >
                  {displayCartCount}
                </span>
              </div>
              <span className="hidden text-sm font-bold sm:inline">Cart</span>
            </Link>

            {/* Mobile Sign In / Sign Up (Replaces Cart Icon on Mobile Responsive) */}
            <div className="flex items-center gap-1 lg:hidden">
              {session ? (
                <Link
                  href={
                    session.user.role === "VENDOR"
                      ? "/vendor"
                      : session.user.role === "ADMIN"
                      ? "/admin"
                      : session.user.role === "ADMIN1"
                      ? "/admin1"
                      : "/dashboard/profile"
                  }
                  className="flex items-center gap-1.5 rounded-xl border border-[#087DB5]/30 bg-[#087DB5]/10 px-2.5 py-1.5 text-xs font-bold text-[#073B70] dark:border-white/10 dark:bg-white/10 dark:text-white hover:bg-[#087DB5]/20 transition-colors"
                >
                  <User className="h-3.5 w-3.5 text-[#087DB5] dark:text-[#00B7B0]" />
                  <span className="max-w-[75px] truncate">{session.user.name?.split(" ")[0] || "Account"}</span>
                </Link>
              ) : (
                <div className="flex items-center rounded-xl border border-[#087DB5]/30 bg-slate-50 p-0.5 dark:border-white/15 dark:bg-slate-800 shadow-2xs">
                  <Link
                    href="/login"
                    className="rounded-lg bg-gradient-to-r from-[#087DB5] to-[#073B70] px-2.5 py-1 text-xs font-bold text-white shadow-2xs hover:opacity-95 transition-opacity"
                  >
                    Sign In
                  </Link>
                  <span className="px-1 text-[11px] font-bold text-slate-300 dark:text-slate-600">/</span>
                  <Link
                    href="/signup"
                    className="rounded-lg px-2 py-1 text-xs font-bold text-[#073B70] dark:text-slate-200 hover:text-[#087DB5] dark:hover:text-white transition-colors"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-2 pb-2 md:hidden">
          <SearchBar compact initialQuery={searchParams.get("q") ?? ""} />
        </div>
      </div>

      {/* Sub-header Navigation Bar in Deep Navy (#073B70) */}
      <div className="relative z-10 bg-[#073B70] text-white shadow-xs border-t border-white/10">
        <nav className="mx-auto flex max-w-[1500px] items-center gap-1 px-3 text-xs sm:text-[13px] overflow-x-auto no-scrollbar py-1.5">
          {/* "All" Button */}
          {navData.showAllButton && (
            <Link
              href="/search"
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 font-bold text-white hover:bg-white/15 transition-colors"
            >
              <Menu className="h-4 w-4 text-[#00B7B0]" />
              <span>All</span>
            </Link>
          )}

          {/* Today's Deals */}
          {navData.specialLinks?.deals?.visible && (
            <Link
              href={navData.specialLinks.deals.href || "/search?deal=flash"}
              className="shrink-0 rounded-lg px-2.5 py-1 text-slate-200 hover:text-[#00B7B0] hover:bg-white/10 transition-colors font-medium whitespace-nowrap"
            >
              {navData.specialLinks.deals.label || "Today's Deals"}
            </Link>
          )}

          {/* Prime */}
          {navData.specialLinks?.prime?.visible && (
            <Link
              href={navData.specialLinks.prime.href || "/prime"}
              className="shrink-0 rounded-lg px-2.5 py-1 font-semibold text-white hover:text-[#00B7B0] hover:bg-white/10 transition-colors"
            >
              {navData.specialLinks.prime.label || "Prime"}
            </Link>
          )}

          {/* Admin-Selected Categories */}
          {navData.categories.map((category) => (
            <Link
              key={category.id || category.name}
              href={category.href}
              className="shrink-0 rounded-lg px-2.5 py-1 text-slate-200 hover:text-[#00B7B0] hover:bg-white/10 transition-colors font-medium whitespace-nowrap"
            >
              {category.name}
            </Link>
          ))}

          {/* Compare */}
          {navData.specialLinks?.compare?.visible && (
            <Link
              href={navData.specialLinks.compare.href || "/compare"}
              className="shrink-0 rounded-lg px-2.5 py-1 text-slate-200 hover:text-[#00B7B0] hover:bg-white/10 transition-colors font-medium"
            >
              {navData.specialLinks.compare.label || "Compare"}
            </Link>
          )}

          {/* Sell Phone / PC */}
          <Link
            href="/sell"
            className="shrink-0 rounded-lg bg-[#FF8A00]/20 px-2.5 py-1 text-[#FF8A00] hover:bg-[#FF8A00] hover:text-white transition-all font-bold whitespace-nowrap"
          >
            Sell Phone / PC
          </Link>

          {/* Custom Links */}
          {(navData.customLinks || [])
            .filter((link) => link.visible)
            .map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className="shrink-0 rounded-lg px-2.5 py-1 text-slate-300 hover:text-white hover:bg-white/10 transition-colors font-medium"
              >
                {link.label}
              </Link>
            ))}
        </nav>
      </div>

      {/* Mobile Drawer Backdrop & Menu */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 top-[106px] z-40 bg-black/50 backdrop-blur-xs lg:hidden animate-in fade-in duration-200"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-50 border-t border-slate-200/60 bg-white p-4 text-[#172B3A] shadow-2xl dark:border-white/10 dark:bg-slate-900 dark:text-white lg:hidden max-h-[calc(100vh-115px)] overflow-y-auto">
          {/* Brand Banner in Mobile Menu */}
          <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3 dark:border-white/10">
            <MovaLogo size="sm" showTagline />
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* 4 Trust Badges Mini Grid in Mobile Menu */}
          <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-2.5 dark:bg-white/5 text-[11px] font-bold">
            <div className="flex items-center gap-1.5 text-[#087DB5]">
              <ShieldCheck className="h-4 w-4 shrink-0" />
              <span>Quality Products</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#00B7B0]">
              <Truck className="h-4 w-4 shrink-0" />
              <span>Fast Delivery</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#FF8A00]">
              <Sparkles className="h-4 w-4 shrink-0" />
              <span>Great Prices</span>
            </div>
            <div className="flex items-center gap-1.5 text-[#073B70] dark:text-teal-300">
              <Package className="h-4 w-4 shrink-0" />
              <span>Secure Payments</span>
            </div>
          </div>


          {/* User Status / Account Section */}
          <div className="mb-4 border-b border-slate-200/60 pb-3 dark:border-white/10">
            {session ? (
              <div className="space-y-1">
                <p className="px-1 text-xs text-slate-500 dark:text-slate-400">
                  Signed in as <strong className="text-[#073B70] dark:text-white">{session.user?.name || session.user?.email}</strong>
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
                  <Link
                    href="/dashboard/profile"
                    className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Your Account
                  </Link>
                  <Link
                    href="/dashboard/orders"
                    className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Your Orders
                  </Link>
                  <Link
                    href="/dashboard/wishlist"
                    className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Wishlist
                  </Link>
                  <Link
                    href="/dashboard/listings"
                    className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    My Listings
                  </Link>
                  <Link
                    href="/sell"
                    className="col-span-2 rounded-lg bg-gradient-to-r from-[#FF8A00] to-[#E57A00] px-3 py-2 text-center font-bold text-white shadow-xs hover:opacity-95"
                    onClick={() => setMenuOpen(false)}
                  >
                    📱 Sell Phone or PC (100% Free)
                  </Link>
                  {session.user.role === "VENDOR" && (
                    <Link
                      href="/vendor"
                      className="col-span-2 rounded-lg bg-[#087DB5] px-3 py-2 text-center font-bold text-white hover:bg-[#073B70]"
                      onClick={() => setMenuOpen(false)}
                    >
                      🏪 Vendor Dashboard
                    </Link>
                  )}
                  {session.user.role === "ADMIN" && (
                    <Link
                      href="/admin"
                      className="col-span-2 rounded-lg bg-[#073B70] px-3 py-2 text-center font-bold text-white hover:bg-[#087DB5]"
                      onClick={() => setMenuOpen(false)}
                    >
                      🛡️ Admin Console (Seller Central)
                    </Link>
                  )}
                  {session.user.role === "ADMIN1" && (
                    <Link
                      href="/admin1"
                      className="col-span-2 rounded-lg bg-[#FF8A00] px-3 py-2 text-center font-bold text-white hover:bg-[#E67C00]"
                      onClick={() => setMenuOpen(false)}
                    >
                      🛡️ Admin 1 Console
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      signOut({ callbackUrl: "/" });
                    }}
                    className="col-span-2 rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 text-center text-sm font-medium hover:bg-red-50 text-red-600 dark:text-red-400"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  href="/login"
                  className="block rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] px-3 py-2.5 text-center font-bold text-white shadow-xs hover:from-[#098dc9] hover:to-[#084582]"
                  onClick={() => setMenuOpen(false)}
                >
                  Sign in
                </Link>
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  New customer?{" "}
                  <Link
                    href="/signup"
                    className="text-[#087DB5] font-bold underline"
                    onClick={() => setMenuOpen(false)}
                  >
                    Start here
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* Quick Categories & Navigation */}
          <div className="mb-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#073B70] dark:text-teal-400">Shop by Category</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link
                href="/search"
                className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20 truncate"
                onClick={() => setMenuOpen(false)}
              >
                All categories
              </Link>
              {navData.specialLinks?.deals?.visible && (
                <Link
                  href={navData.specialLinks.deals.href || "/search?deal=flash"}
                  className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20 truncate"
                  onClick={() => setMenuOpen(false)}
                >
                  {navData.specialLinks.deals.label || "Today's Deals"}
                </Link>
              )}
              {navData.categories.map((c) => (
                <Link
                  key={c.id}
                  href={c.href}
                  className="rounded-lg bg-slate-100 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-200 dark:hover:bg-white/20 truncate"
                  onClick={() => setMenuOpen(false)}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Official Contacts in Mobile Menu */}
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs dark:border-white/10 dark:bg-white/5 space-y-1.5">
            <p className="font-bold text-[#073B70] dark:text-white">Customer Support &amp; Contact:</p>
            <div className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
              <div className="flex flex-wrap items-center gap-1 text-[#087DB5] font-semibold">
                <Phone className="h-3.5 w-3.5 text-[#00B7B0]" />
                <span>Contact:</span>
                <a href="tel:+251931463406" className="hover:underline">+251 93 146 3406</a>
                <span>/</span>
                <a href="tel:+251995871028" className="hover:underline">+251 99 587 1028</a>
              </div>
            </div>
          </div>

          {/* App Controls: Theme & Language */}
          <div className="flex items-center gap-2 border-t border-slate-200/60 pt-3 text-sm dark:border-white/10">
            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-100 dark:bg-white/10 py-2.5 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
            >
              {mounted && theme === "dark" ? <Sun className="h-4 w-4 text-[#FF8A00]" /> : <Moon className="h-4 w-4 text-[#073B70]" />}
              <span>{mounted && theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </button>
            <button
              type="button"
              onClick={() => setLang((l) => (l === "EN" ? "AM" : "EN"))}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-slate-100 dark:bg-white/10 px-4 py-2.5 font-medium hover:bg-slate-200 dark:hover:bg-white/20"
            >
              <Globe className="h-4 w-4 text-[#087DB5]" />
              <span>{lang}</span>
            </button>
          </div>
        </div>
      </>
    )}
  </header>
);
}
